// import dependencies
import React, { Fragment } from 'react';
import { observer, inject } from 'mobx-react';

import Checkbox from '~/common/components/Checkbox';
import Trust from '~/common/images/img-trust-arc.svg';
import getPrices from '~/common/utils/prices';
import generateLabelLink from '~/common/utils/generateLabelLink';

// import stylesheet
import styles from './order-summary.css';

@inject(
    'checkoutStore', 'formsStore', 'contentStore'
)
@observer
export default class OrderSummary extends React.Component {
    originalTotal = () => {
        const { selectedPlan, isAnnual } = this.props.checkoutStore;
        const { LicenseSelector } = this.props.formsStore;
        const { monthlyPrice } = getPrices(selectedPlan.Prices);

        return isAnnual ? (LicenseSelector * monthlyPrice.PerMonthPrice) * 12 : LicenseSelector * monthlyPrice.PerMonthPrice
    }

    addonPrice = (addon) => {
        const { monthlyPrice } = getPrices(addon.Prices);
        let price = addon.Billing === 'YEAR' ? monthlyPrice.PerMonthPrice * 12 : monthlyPrice.PerMonthPrice;
        if (addon.Quantity === null) price *= this.props.formsStore.LicenseSelector;
        if (monthlyPrice.UsagePrice > 0 && monthlyPrice.PerMonthEffectivePrice === 0) price = monthlyPrice.UsagePrice;
        return price;
    };
    hasPromo = (data, isAnnual) => {
        const { monthlyPrice, annualPrice } = getPrices(data.Prices);
        const pricing = isAnnual ? annualPrice : monthlyPrice;
        return pricing.PerMonthPrice !== pricing.PerMonthEffectivePrice;
    }
    promoText = (data) => {
        const { checkoutStore } = this.props;
        const { labelContent } = this.props.contentStore;
        const { BillingPeriod, LicenseSelector } = this.props.formsStore;
        const { monthlyPrice, annualPrice } = getPrices(data.Prices);
        let pricing;
        let promoString;
        const frequency = data.Billing ? data.Billing : BillingPeriod;
        if (frequency === 'YEAR') {
            pricing = annualPrice;
            promoString = labelContent.AmountOffAnnualPromotionalDiscount;
        } else {
            pricing = monthlyPrice;
            promoString = labelContent.AmountOffAnnualPromotionalDiscount;
        }
        const promoPercent = checkoutStore.percentage(pricing.PerMonthPrice, pricing.PerMonthEffectivePrice);
        let promoSaving = (pricing.PerMonthPrice) - (pricing.PerMonthEffectivePrice);
        if (frequency === 'YEAR') {
            promoSaving *= 12;
        }

        data.Quantity ? promoSaving = promoSaving * data.Quantity : promoSaving = promoSaving * LicenseSelector

        if (pricing.PerMonthPrice !== pricing.PerMonthEffectivePrice) {
            return (
                <Fragment>
                    <div className={`${styles.row} ${styles['row--pad-top']}`}>
                        <span className={styles.green}>{promoString.replace('{discountAmountPercentage}', `${promoPercent}%`)}</span>
                        <span className={styles.green}>-{checkoutStore.currency(promoSaving)}</span>
                    </div>
                </Fragment>
            );
        }
    }

    fullYearPrice = () => {
        const { selectedPlan, selectedAddons, isAnnual } = this.props.checkoutStore;
        const { LicenseSelector } = this.props.formsStore;
        const { annualPrice } = getPrices(selectedPlan.Prices);

        let yearTotal = 0;

        if (isAnnual) {
            yearTotal += annualPrice.PerMonthPrice * 12 * LicenseSelector;
        }

        selectedAddons.forEach((addon) => {
            const { monthlyPrice: addonMonthlyPrice, annualPrice: addonAnnualPrice } = getPrices(addon.Prices);

            if (addon.Billing === 'YEAR') {
                let price = addonAnnualPrice.PerMonthPrice * 12;
                price = addon.Quantity ? price * addon.Quantity : price * LicenseSelector;

                if (addonMonthlyPrice.UsagePrice > 0 && addonMonthlyPrice.PerMonthEffectivePrice === 0) price = addonMonthlyPrice.UsagePrice;
                yearTotal += price;
            }
        });

        return yearTotal;
    }

    fullMonthPrice = () => {
        const { selectedPlan, selectedAddons, isAnnual } = this.props.checkoutStore;
        const { LicenseSelector } = this.props.formsStore;
        const { monthlyPrice } = getPrices(selectedPlan.Prices);

        let monthTotal = 0;

        if (!isAnnual) {
            monthTotal += monthlyPrice.PerMonthPrice * (selectedPlan.Quantity ? selectedPlan.Quantity : LicenseSelector);
        }

        if (selectedAddons) {
            selectedAddons.map((addon) => {
                const { monthlyPrice } = getPrices(addon.Prices);

                if (addon.Billing !== 'YEAR') {
                    let price = monthlyPrice.PerMonthPrice;
                    addon.Quantity ? price *= addon.Quantity : price *= LicenseSelector;
                    monthTotal += price;
                }
            })
        }

        return monthTotal;
    }

    annualSavingsPercentage = (data) => {
        const { labelContent } = this.props.contentStore;
        const { monthlyPrice, annualPrice } = getPrices(data.Prices);

        const percent = Math.floor((((monthlyPrice.PerMonthPrice - annualPrice.PerMonthPrice) / monthlyPrice.PerMonthPrice) * 100).toFixed(5));

        return labelContent.SaveAmountWithAnnualPlan.replace('{discountAmountPercentage}', `${percent}%`);
    }

    annualSaving = () => {
        const { selectedPlan } = this.props.checkoutStore;
        const { LicenseSelector } = this.props.formsStore;
        const { monthlyPrice, annualPrice } = getPrices(selectedPlan.Prices);

        return Math.floor(((monthlyPrice.PerMonthPrice - annualPrice.PerMonthPrice) * 12) * LicenseSelector);
    }

    getPromoSaving = (prices, isAnnual) => {
        const { LicenseSelector } = this.props.formsStore;
        const { monthlyPrice, annualPrice } = getPrices(prices);
        const pricing = isAnnual ? annualPrice : monthlyPrice;
        let promoSaving = (pricing.PerMonthPrice) - (pricing.PerMonthEffectivePrice);
        if (isAnnual) {
            promoSaving *= 12;
        }
        return promoSaving * LicenseSelector;
    }

    getTotalSavings = () => {
        const { isAnnual, selectedAddons, selectedPlan } = this.props.checkoutStore;
        let amount = isAnnual ? this.annualSaving() : 0;
        amount += this.getPromoSaving(selectedPlan.Prices, isAnnual);

        selectedAddons.forEach((addon) => {
            const addonIsAnnual = addon.Billing === 'YEAR';
            amount += this.getPromoSaving(addon.Prices, addonIsAnnual);
            if (addonIsAnnual) {
                const { monthlyPrice: addonMonthlyPrice, annualPrice: addonAnnualPrice } = getPrices(addon.Prices);
                amount += Math.floor((addonMonthlyPrice.PerMonthPrice * 12) - (addonAnnualPrice.PerMonthPrice * 12));
            }
        });
        return this.props.checkoutStore.currency(amount);
    }

    render() {
        const { checkoutStore, formsStore, mobile } = this.props;
        const { labelContent, contentData } = this.props.contentStore;
        const { selectedPlan, productPrice, total, isAnnual, selectedAddons, selectedProduct, monthDate, endDate, totalMonthPrice, totalYearPrice } = this.props.checkoutStore;
        const { LicenseSelector, DisplayOptIn } = this.props.formsStore;
        const openVoice = selectedPlan.ProductFamilyKey === 'OPENVOICE';
        const { monthlyPrice } = getPrices(selectedPlan.Prices);
        const hasSavings = isAnnual || !!selectedAddons.find(a => a.Billing === 'YEAR') || this.hasPromo(selectedPlan);

        return (
            <div className={`${styles['order-summary']} ${mobile ? styles['order-summary--mobile'] : ''}`} ref="orderSummary">
                <div className={styles.wrapper}>
                    <h2>
                        {labelContent.OrderSummary}
                    </h2>
                    {/* Product name and total before discounts */}
                    <div className={`${styles.section} ${styles['section--border-bottom']}`}>
                        <div className={styles.row}>
                            <span className={styles.bold}>{selectedProduct} {selectedPlan.Name}</span>
                            {!openVoice &&
                                <span className={styles.bold}>{checkoutStore.currency(this.originalTotal())}</span>
                            }
                        </div>
                        {/* Number of organizers */}
                        <div className={styles.row}>
                            {!openVoice && <span className={styles.light}>{LicenseSelector} {LicenseSelector > 1 ? labelContent.Organizers : labelContent.Organizer}</span>}
                        </div>
                        {/* Price breakdown */}
                        <div className={styles.row}>
                            {openVoice ?
                                <span className={styles.light}>{checkoutStore.currency(monthlyPrice.UsagePrice)} /{labelContent.Minute} /{labelContent.Caller}</span>
                                :
                                <span className={styles.light}>{isAnnual ? (checkoutStore.currency(monthlyPrice.PerMonthPrice * 12)) : checkoutStore.currency(productPrice)} /{labelContent.Organizer} /{isAnnual ? labelContent.Year : labelContent.Month}</span>
                            }
                        </div>
                        {openVoice &&
                            <div className={styles.row}>
                                <span className={styles.light}>{labelContent.BillingStartsNextMonth}</span>
                            </div>
                        }
                        {/* annual savings */}
                        {!!openVoice || isAnnual &&
                            <div className={`${styles.row} ${styles['row--pad-top']}`}>
                                <span className={styles.green}>{this.annualSavingsPercentage(selectedPlan)}</span>
                                <span className={styles.green}>-{checkoutStore.currency(this.annualSaving())}</span>
                            </div>
                        }
                        {/* promotional savings */}
                        {!!openVoice || this.promoText(selectedPlan)}
                    </div>
                    {/* Addons */}
                    {selectedAddons.map((addOn, i) => {
                        const { monthlyPrice: addonMonthlyPrice, annualPrice: addonAnnualPrice } = getPrices(addOn.Prices);
                        return (<div className={`${styles.section} ${styles['section--border-bottom']}`} key={i}>
                            {/* Addon Name and monthly price before discount, multiplied by the number of organizers */}
                            <div className={styles.row}>
                                <span className={styles.bold}>{addOn.Name}</span>
                                <span className={styles.bold}>
                                    {this.addonPrice(addOn) !== addonMonthlyPrice.UsagePrice &&
                                        checkoutStore.currency(this.addonPrice(addOn) * (addOn.Quantity ? addOn.Quantity : LicenseSelector))
                                    }
                                </span>
                            </div>
                            {/* Price breakdown */}
                            <div className={styles.row}>
                                <span className={styles.light}>
                                    {addOn.Quantity ? addOn.Quantity : LicenseSelector} {LicenseSelector > 1 && addOn.Quantity !== 1 ? labelContent.Organizers : labelContent.Organizer}
                                </span>
                            </div>
                            <div className={styles.row}>
                                {addonMonthlyPrice.UsagePrice > 0 && addonMonthlyPrice.PerMonthEffectivePrice === 0 ?
                                    <span className={styles.light}>{addonMonthlyPrice.UsagePrice} /{labelContent.Minute} /{labelContent.Caller}</span>
                                    :
                                    <span className={styles.light}>{checkoutStore.currency(this.addonPrice(addOn))} /{labelContent.Organizer} /{addOn.Billing === 'YEAR' ? 'Year' : 'Month'}</span>
                                }
                            </div>
                            {/* Annual savings */}
                            {addOn.Billing === 'YEAR' &&
                                <div className={`${styles.row} ${styles['row--pad-top']}`}>
                                    <span className={styles.green}>
                                        {this.annualSavingsPercentage(addOn)}
                                    </span>
                                    <span className={styles.green}>
                                        -{checkoutStore.currency(Math.floor((addonMonthlyPrice.PerMonthPrice * 12) - (addonAnnualPrice.PerMonthPrice * 12)))}
                                    </span>
                                </div>
                            }
                            {addOn.BillingStartsNextMonth &&
                                <div className={styles.row}>
                                    <span className={styles.light}>{labelContent.BillingStartsNextMonth}</span>
                                </div>
                            }
                            {/* Promotional discount */}
                            {addonMonthlyPrice.PerMonthEffectivePrice > 0 &&
                                this.promoText(addOn)
                            }
                            <button data-remove={addOn.Name} onClick={() => checkoutStore.selectAddon(addOn, addOn.Billing)} type="button">({labelContent.Remove})</button>
                        </div>);
                    })}
                    {/* Total price */}
                    <div className={styles.section}>
                        <div className={`${styles.row}  ${styles['row--section']} ${styles.total}`}>
                            <span>
                                {labelContent.Total} {contentData.CurrencyCode}
                            </span>
                            <span>{checkoutStore.currency(total)}</span>
                        </div>
                        <div className={`${styles.row}  ${styles['row--section']}`}>
                            <span> </span>
                            <span className={styles.light}>({labelContent.BeforeTaxes})</span>
                        </div>
                        {hasSavings &&
                            <div className={`${styles.row} ${styles['row--no-margin']}`}>
                                <span className={`${styles.bold} ${styles.green}`}>
                                    {labelContent.TotalSavings}
                                </span>
                                <span className={`${styles.bold} ${styles.green}`}>
                                    {this.getTotalSavings()}
                                </span>
                            </div>
                        }
                    </div>
                    {!openVoice &&
                        <div className={`${styles.section} ${styles['section--border-bottom']}`}>
                            {totalMonthPrice > 0 &&
                                <Fragment>
                                    <div className={`${styles.row}`}>
                                        <span>
                                            {this.hasPromo(selectedPlan, false) ? `*${labelContent.RecurringMonthlyTotalWhenPromotionEnds}` : labelContent.RecurringMonthlyTotal}
                                        </span>
                                        <span className={styles.nowrap}>{checkoutStore.currency(this.fullMonthPrice())}</span>
                                    </div>
                                    <div className={`${styles.row}`}>
                                        <span className={styles.light}>
                                            {labelContent.AutoRenewalOnDate.replace('{autoRenewalDate}', monthDate)}
                                        </span>
                                    </div>
                                </Fragment>
                            }
                            {totalYearPrice > 0 && totalMonthPrice > 0 &&
                                <div className={`${styles.row}`} />
                            }
                            {totalYearPrice > 0 &&
                                <Fragment>
                                    <div className={`${styles.row}`}>
                                        <span>
                                            {this.hasPromo(selectedPlan, true) ? `*${labelContent.RecurringAnnualTotalWhenPromotionEnds}` : labelContent.RecurringAnnualTotal}
                                        </span>
                                        <span className={styles.nowrap}>{checkoutStore.currency(this.fullYearPrice())}</span>
                                    </div>
                                    <div className={`${styles.row}`}>
                                        <span className={styles.light}>
                                            {labelContent.AutoRenewalOnDate.replace('{autoRenewalDate}', endDate)}
                                        </span>
                                    </div>
                                </Fragment>
                            }
                        </div>
                    }

                    {openVoice &&
                        <div className={styles.section}>
                            <div className={styles.terms} dangerouslySetInnerHTML={{ __html: labelContent.Footnotes }} />
                        </div>
                    }

                    <div className={styles.section}>
                        <div className={styles.terms}>{labelContent.TaxInformation}</div>
                    </div>

                    <div className={styles.terms}>
                        {generateLabelLink(labelContent.UserAgreement, e => formsStore.setDisplayOptIn(e, true))}
                    </div>
                    {(DisplayOptIn || !contentData.OptInByDefault) &&
                    <div className={styles.optout}>
                        <Checkbox
                            checked={formsStore.OptOut}
                            id="IsNewsletter"
                            name="IsNewsletter"
                            onClick={() => formsStore.toggleNewsletter()}
                        />
                        <label htmlFor="isNewsletter">{labelContent.MailingListPreference}</label>
                    </div>
                    }
                    <a href="https://privacy.truste.com/privacy-seal/validation?rid=2853fc28-1c77-46f4-8ff8-e4289f5e569b" target="_blank"><img className={styles.trust} src={Trust} alt="TRUSTe"/></a>
                </div>
            </div>
        );
    }
}
